import React, { useState, useEffect } from 'react';
import { Upload, Edit2, Trash2, Star, X } from 'lucide-react';
import { logoAPI, uploadAPI } from '../../../../services/dashboardAPI';

const LogoManagement = ({ showNotification }) => {
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    companyName: '',
    logoUrl: '',
    website: '',
    description: '',
    category: 'partner',
  });

  useEffect(() => {
    fetchLogos();
  }, []);

  const fetchLogos = async () => {
    try {
      setLoading(true);
      const response = await logoAPI.getAll();
      if (response.success) {
        setLogos(response.data);
      } else {
        showNotification('Failed to fetch logos', 'error');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadLogo = async () => {
    if (!logoFile) return null;
    
    try {
      setUploading(true);
      const response = await uploadAPI.uploadFile(logoFile, 'logos');
      if (response.success) {
        return response.data.url;
      } else {
        showNotification('Logo upload failed', 'error');
        return null;
      }
    } catch (error) {
      showNotification(error.message, 'error');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleSaveLogo = async () => {
    if (!formData.companyName) {
      showNotification('Company name is required', 'error');
      return;
    }

    try {
      setLoading(true);
      let logoUrl = formData.logoUrl;
      
      if (logoFile) {
        logoUrl = await uploadLogo();
        if (!logoUrl) {
          setLoading(false);
          return;
        }
      }

      const logoPayload = {
        ...formData,
        logoUrl,
      };

      let response;
      if (editingId) {
        response = await logoAPI.update(editingId, logoPayload);
      } else {
        response = await logoAPI.create(logoPayload);
      }

      if (response.success) {
        showNotification(editingId ? 'Logo updated!' : 'Logo added!');
        setShowForm(false);
        setEditingId(null);
        setFormData({
          companyName: '',
          logoUrl: '',
          website: '',
          description: '',
          category: 'partner',
        });
        setLogoFile(null);
        setLogoPreview(null);
        fetchLogos();
      } else {
        showNotification('Failed to save logo', 'error');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (logo) => {
    setFormData(logo);
    setLogoPreview(logo.logoUrl);
    setEditingId(logo.id);
    setShowForm(true);
    setLogoFile(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this logo?')) {
      try {
        setLoading(true);
        const response = await logoAPI.delete(id);
        if (response.success) {
          showNotification('Logo deleted!');
          fetchLogos();
        } else {
          showNotification('Failed to delete logo', 'error');
        }
      } catch (error) {
        showNotification(error.message, 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      setLoading(true);
      const response = await logoAPI.toggleFeatured(id);
      if (response.success) {
        showNotification('Featured status updated!');
        fetchLogos();
      } else {
        showNotification('Failed to update', 'error');
      }
    } catch (error) {
      showNotification(error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({
      companyName: '',
      logoUrl: '',
      website: '',
      description: '',
      category: 'partner',
    });
    setLogoFile(null);
    setLogoPreview(null);
  };

  return (
    <>
      <div className="section-header">
        <div>
          <h2 className="title">Company Logos</h2>
          <p className="subtitle">Manage partner company logos</p>
        </div>
        <button 
          className="btn-primary"
          onClick={() => setShowForm(!showForm)}
          disabled={loading}
        >
          <Upload size={18} /> Add Logo
        </button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="form-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3>{editingId ? 'Edit Logo' : 'Add New Logo'}</h3>
            <button onClick={handleCloseForm} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <X size={24} />
            </button>
          </div>

          <div className="form-group">
            <label>Company Name *</label>
            <input 
              type="text"
              placeholder="Enter company name"
              value={formData.companyName}
              onChange={(e) => setFormData({...formData, companyName: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Website</label>
            <input 
              type="url"
              placeholder="https://example.com"
              value={formData.website}
              onChange={(e) => setFormData({...formData, website: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Category</label>
            <input 
              type="text"
              placeholder="e.g., sponsor, partner, vendor"
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea 
              placeholder="Company description"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label>Logo Image *</label>
            <div style={{ marginBottom: '10px' }}>
              <input 
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ marginBottom: '10px' }}
              />
            </div>
            {logoPreview && (
              <div style={{ marginTop: '10px' }}>
                <img 
                  src={logoPreview} 
                  alt="Preview" 
                  style={{ maxWidth: '150px', maxHeight: '100px', borderRadius: '8px' }}
                />
                {logoFile && (
                  <button 
                    onClick={() => { setLogoFile(null); setLogoPreview(null); }}
                    style={{ marginLeft: '10px', background: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="form-actions">
            <button className="btn-secondary" onClick={handleCloseForm} disabled={loading}>
              Cancel
            </button>
            <button 
              className="btn-primary" 
              onClick={handleSaveLogo}
              disabled={loading || uploading}
            >
              {loading || uploading ? 'Saving...' : editingId ? 'Update' : 'Add'}
            </button>
          </div>
        </div>
      )}

      {/* Logos Table */}
      {loading && !showForm ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : logos.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>No logos found</div>
      ) : (
        <div className="logos-table">
          <table>
            <thead>
              <tr>
                <th>Logo</th>
                <th>Company Name</th>
                <th>Category</th>
                <th>Featured</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {logos.map((logo) => (
                <tr key={logo.id}>
                  <td>
                    {logo.logoUrl && (
                      <img 
                        src={logo.logoUrl} 
                        alt={logo.companyName}
                        style={{ maxWidth: '80px', maxHeight: '60px' }}
                      />
                    )}
                  </td>
                  <td><strong>{logo.companyName}</strong></td>
                  <td>{logo.category}</td>
                  <td>
                    <button 
                      className={`toggle-btn ${logo.featured ? 'active' : ''}`}
                      onClick={() => handleToggleFeatured(logo.id)}
                      disabled={loading}
                    >
                      <Star size={16} fill={logo.featured ? '#861b68' : 'none'} />
                    </button>
                  </td>
                  <td className="table-actions">
                    <button 
                      className="icon-btn edit" 
                      onClick={() => handleEdit(logo)}
                      disabled={loading}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button 
                      className="icon-btn delete"
                      onClick={() => handleDelete(logo.id)}
                      disabled={loading}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default LogoManagement;
